import { replace } from 'connected-react-router'
import React, { useCallback } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import CreateModalFooter from './CreateModalFooter'
import { CreateItemProps } from './index'
import {
  CreateBusItemViewModel,
  CreateFerryItemViewModel,
  CreateOtherTransportItemViewModel,
  CreateTrainItemViewModel,
} from './models'
import {
  createFerryPayloadFromViewModel,
  createOtherTransportPayloadFromViewModel,
  createTrainPayloadFromViewModel,
  createBusPayloadFromViewModel,
} from './utils'
import BusFields from '../Common/BusFields'
import FerryFields from '../Common/FerryFields'
import { formOptions } from '../Common/Fields/config'
import OtherTransportFields from '../Common/OtherTransportFields'
import TrainFields from '../Common/TrainFields'

import Select from 'components/Common/Form/Input/Select'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useCreateTripItem } from 'tripPlanner/hooks/api'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import { TripItem } from 'tripPlanner/types/tripItem'
import { itemSectionId } from 'tripPlanner/utils/itemSection'
import { getSelectedItineraryDate } from 'tripPlanner/utils/selectors'

type CreateTransportItemViewModel =
  | CreateTrainItemViewModel
  | CreateBusItemViewModel
  | CreateOtherTransportItemViewModel
  | CreateFerryItemViewModel

const SELECT_OPTIONS: {
  value: CreateTransportItemViewModel['type']
  label: string
}[] = [
  { value: 'OTHER_TRANSPORT', label: 'Private Transport' },
  { value: 'TRAIN', label: 'Train' },
  { value: 'BUS', label: 'Bus' },
  { value: 'FERRY', label: 'Ferry' },
]

function CreateTransport(props: CreateItemProps) {
  const { tripId } = props
  const selectedDate = useAppSelector<string | undefined>(
    getSelectedItineraryDate,
  )

  const { closeModal, goBack } = useModalCallbacks()

  const methods = useForm<CreateTransportItemViewModel>({
    ...formOptions,
    defaultValues: {
      type: 'OTHER_TRANSPORT',
      sourceType: 'NA',
      isBooked: false,
      isEndDateSame: true,
      startDate: selectedDate,
      endDate: selectedDate,
      startPlace: {},
      endPlace: {},
    },
  })
  const { handleSubmit, control, watch } = methods
  const dispatch = useAppDispatch()

  const type = watch('type')

  const { mutate: createMutate, isLoading } = useCreateTripItem({
    onSuccess: ({ id }: TripItem) => {
      dispatch(replace({ hash: itemSectionId(id) }))
      closeModal()
    },
  })

  const createTransportItem = useCallback(
    (viewModel: CreateTransportItemViewModel) => {
      switch (viewModel.type) {
        case 'TRAIN':
          createMutate({
            tripId,
            tripItem: createTrainPayloadFromViewModel(viewModel),
          })
          break
        case 'BUS':
          createMutate({
            tripId,
            tripItem: createBusPayloadFromViewModel(viewModel),
          })
          break
        case 'OTHER_TRANSPORT':
          createMutate({
            tripId,
            tripItem: createOtherTransportPayloadFromViewModel(viewModel),
          })
          break
        case 'FERRY':
          createMutate({
            tripId,
            tripItem: createFerryPayloadFromViewModel(viewModel),
          })
          break
      }
    },
    [createMutate, tripId],
  )

  return (
    <>
      <ModalHeader
        title="Add transport"
        onBackButtonClick={goBack}
        onCloseButtonClick={closeModal}
      />
      <ModalBody>
        <FormProvider {...methods}>
          <ModalContent>
            <Controller
              name="type"
              control={control}
              render={({ field }) => {
                return (
                  <Select {...field} label="Type" required>
                    {SELECT_OPTIONS.map((opt) => (
                      <option
                        key={opt.value}
                        value={opt.value}
                        label={opt.label}
                      >
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                )
              }}
            />
            {type === 'TRAIN' && <TrainFields tripId={tripId} />}
            {type === 'BUS' && <BusFields tripId={tripId} />}
            {type === 'FERRY' && <FerryFields tripId={tripId} />}
            {type === 'OTHER_TRANSPORT' && (
              <OtherTransportFields tripId={tripId} />
            )}
          </ModalContent>
        </FormProvider>
      </ModalBody>
      <CreateModalFooter
        onSubmit={handleSubmit(createTransportItem)}
        isLoading={isLoading}
      />
      {isLoading && <LoadingIndicator floating />}
    </>
  )
}

export default CreateTransport
